import { NgClass } from '@angular/common';
import { Component, inject, signal, type WritableSignal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { take, timer } from 'rxjs';

import { DiagnosticRating } from '@malou-io/package-utils';

import { RatingChipComponent } from ':modules/rating-chip/rating-chip.component';
import { LoaderProgressComponent } from ':shared/components/loader-progress/loader-progress.component';
import { DELAY_TO_CLOSE_MODAL, DELAY_TO_START_NUMBER_ANIMATION, FIRE_CONFETTI_INTERVAL } from ':shared/constants';
import { SvgIcon } from ':shared/enums';
import { CELEBRATION_CONFETTI_CONFIGURATION, launchConfetti, SAD_CONFETTI_CONFIGURATION } from ':shared/helpers';
import { Illustration, IllustrationPathResolverPipe } from ':shared/pipes';

@Component({
    selector: 'app-restaurant-rank-modal',
    templateUrl: './restaurant-rank-modal.component.html',
    styleUrls: ['./restaurant-rank-modal.component.scss'],
    imports: [MatIconModule, NgClass, TranslateModule, IllustrationPathResolverPipe, LoaderProgressComponent, RatingChipComponent],
})
export class RestaurantRankModalComponent {
    readonly SvgIcon = SvgIcon;
    readonly Illustration = Illustration;
    readonly DiagnosticRating = DiagnosticRating;

    readonly restaurantName: WritableSignal<string> = signal('');
    readonly restaurantAddress: WritableSignal<string> = signal('');
    readonly totalScore: WritableSignal<number> = signal(0);
    readonly totalScoreRating: WritableSignal<DiagnosticRating> = signal(DiagnosticRating.BAD);
    readonly rank: WritableSignal<number> = signal(0);
    readonly shouldShowCrown: WritableSignal<boolean> = signal(false);

    readonly currentValue = signal(0);

    readonly data = inject<{
        restaurantName: string;
        restaurantAddress: string;
        totalScore: number;
        totalScoreRating: DiagnosticRating;
        rank: number;
    }>(MAT_DIALOG_DATA);
    private readonly _dialogRef = inject(MatDialogRef<RestaurantRankModalComponent>);

    constructor() {
        this.restaurantName.set(this.data.restaurantName);
        this.restaurantAddress.set(this.data.restaurantAddress);
        this.totalScore.set(this.data.totalScore);
        this.totalScoreRating.set(this.data.totalScoreRating);
        this.rank.set(this.data.rank);
        setTimeout(() => {
            this._animateTotalScore();
        }, DELAY_TO_START_NUMBER_ANIMATION);
    }

    private _animateTotalScore() {
        const interval = setInterval(() => {
            if (this.currentValue() < this.totalScore()) {
                this.currentValue.set(this.currentValue() + 1);
            } else {
                this.shouldShowCrown.set(this.rank() === 1);
                if (this.totalScoreRating() === DiagnosticRating.GOOD) {
                    timer(0, FIRE_CONFETTI_INTERVAL)
                        .pipe(take(2))
                        .subscribe(() => {
                            launchConfetti(CELEBRATION_CONFETTI_CONFIGURATION);
                        });
                }
                if ([DiagnosticRating.BAD, DiagnosticRating.VERY_BAD].includes(this.totalScoreRating()) && this.rank() !== 1) {
                    timer(0, FIRE_CONFETTI_INTERVAL)
                        .pipe(take(1))
                        .subscribe(() => {
                            launchConfetti(SAD_CONFETTI_CONFIGURATION);
                        });
                }
                clearInterval(interval);
                setTimeout(() => {
                    this.close();
                }, DELAY_TO_CLOSE_MODAL);
            }
        }, 20);
    }

    close(): void {
        this._dialogRef.close();
    }
}
